import React, { useState, useEffect, useRef, useCallback } from 'react';
import Dropdown from '../../../Components/CustomDropdown/customdropdown';
import { useToast } from '../../../Utils/toastContext';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmContinueButton from '../../../Components/ContinueButton/continue-button';
import { updateFormData } from '../../../Redux/store/onboardingFormSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import AddUserPopup from '../../../Components/add-joint-accounts/Add-User-Popup';
import Select, { components } from 'react-select';
import { Plus, Minus, Trash2 } from 'lucide-react';
import ConfirmResetButton from '../../../Components/ContinueButton/reset-button';
import ReactFlow, { MiniMap, Controls, Background } from 'reactflow';
import 'reactflow/dist/style.css';
import TrusteesPanel from '../../../Components/onboarding/trustee-panel';
import MindmapTree from '../../../Components/Onbaording/onboarding-tree';
import {
  setStepStatus,
  setRejectedFields,
  clearStepStatusShareholder,
  clearCurrentStepShareholder,
} from '../../../Redux/store/onboardingFormSlice';
import {
  setApplicationTypeShareHolder,
  clearApplicationTypeShareHolder,
} from '../../../Redux/store/accountTypeSlice';
import { debounce } from 'lodash';
// Helper function to determine if ECDD is pending
const hasECDDPending = (shareholder) => {
  return shareholder?.hasPending || shareholder?.has_pending_edd || false;
};
const ShareholderLayer = ({
  layerName,
  shareholdersForLayer = [],
  rootEntities = [], // ✅ DEFINE PROP
  collapsed = false,
  shareholderTypesData = [],
  onToggle,
  getLayeredData,
}) => {
  const isOpen = !collapsed; // fully controlled
  const dispatch = useDispatch();
  const isRoot = shareholdersForLayer.length === 0;
  const navigate = useNavigate();
  const username = useSelector((state) => state.user.username);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  // First layer shareholders
  const [shareholders, setShareholders] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  // Controls state
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Sub-layer shareholders (per tab)
  const [layerData, setLayerData] = useState(() => {
    let initial = {};
    shareholdersForLayer.forEach((s, idx) => {
      initial[idx] = [
        {
          id: '',
          name: '',
          type: 1,
          shares: '',
          userId: null,
          hasPending: false,
          email: '',
          errors: {},
        },
      ];
    });
    return { [layerName]: initial };
  });
  const addShareholderapi = async (tabIdx) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/onboarding/trust/trustee-beneficiaries/beneficiaries`;
    let payload = {
      beneficiaries:
        layerData[layerName]?.[tabIdx]?.length > 0
          ? layerData[layerName][tabIdx].map((item) => ({
              name: item.name,
              email: item.email,
              stakeholder_type: item.type,
              percentage_ownership: item.shares,
            }))
          : shareholders.map((item) => ({
              name: item.name,
              email: item.email,
              stakeholder_type: item.type,
              percentage_ownership: item.shares,
            })),
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleAddShareholders = useCallback(
    debounce(async (e, tabIdx = null) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await addShareholderapi(tabIdx);
        if (response.status == 200) {
          showToast(response.message, 'success');
          handleFillApplication(tabIdx);
          getLayeredData();
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [shareholders], // Dependencies
  );

  const calculateSharesCounter = (list) => {
    const total = list.reduce((acc, s) => acc + Number(s.shares || 0), 0);
    return `${total.toFixed(2)}`;
  };

  /** ----------------------
   * Root Layer Handlers
   * ---------------------- */
  const updateRootShareholder = (id, field, value, errors = {}) => {
    setShareholders((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value, errors } : s)),
    );
  };

  const addRootShareholder = () => {
    setShareholders((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: '',
        type: 1,
        userId: null,
        shares: 0,
        email: '',
        hasPending: false,
      },
    ]);
  };

  const removeRootShareholder = (id) => {
    setShareholders((prev) => prev.filter((s) => s.id !== id));
  };
  useEffect(() => {
    setActiveTab(0);

    if (!isRoot) {
      const initial = {};

      shareholdersForLayer.forEach((tab, idx) => {
        initial[idx] = tab.data.map((s) => ({
          id: s.beneficiary_user_id,
          name: s.name,
          userId: s.beneficiary_user_id,
          type: s.stakeholder_type.id,
          shares: Number(s.percentage_ownership),
          hasPending: s.has_pending_edd,
          email: s.email,
          errors: {},
        }));
      });

      setLayerData({ [layerName]: initial });
    }
  }, [shareholdersForLayer]);

  useEffect(() => {
    if (shareholders.length > 0) {
      dispatch(updateFormData({ field: 'shareholders', value: shareholders }));
    }
  }, [shareholders]);
  useEffect(() => {
    if (isRoot && rootEntities?.length) {
      setShareholders(
        rootEntities.map((s) => ({
          id: s.beneficiary_user_id,
          userId: s.beneficiary_user_id,
          name: s.name || '',
          type: s.stakeholder_type?.id ?? 1,
          shares: Number(s.percentage_ownership || 0),
          hasPending: s.has_pending_edd,
          email: s.email || '',
          errors: {},
        })),
      );
    }
  }, [isRoot, rootEntities]);
  useEffect(() => {
    if (layerData != null) {
      dispatch(updateFormData({ field: 'layerData', value: layerData }));
    }
  }, [layerData]);

  useEffect(() => {
    dispatch(clearApplicationTypeShareHolder());
    dispatch(clearStepStatusShareholder());
    dispatch(clearCurrentStepShareholder());
  }, []);
  /** ----------------------
   * Sub-layer Handlers
   * ---------------------- */
  const updateShareholder = (tabIdx, idx, field, value, errors = {}) => {
    setLayerData((prev) => {
      const copy = { ...prev };

      // Ensure layerName exists
      if (!copy[layerName]) copy[layerName] = {};

      // Ensure tabIdx exists
      if (!Array.isArray(copy[layerName][tabIdx])) {
        copy[layerName][tabIdx] = [];
      }

      // Clone current tab data
      const tabData = [...copy[layerName][tabIdx]];
      const current = tabData[idx] || {};

      // Ensure value is safe for string fields
      tabData[idx] = {
        ...current,
        [field]: typeof value === 'string' ? value : '',
        errors,
      };

      // Assign back
      copy[layerName] = { ...copy[layerName], [tabIdx]: tabData };

      return copy;
    });
  };
  const addShareholder = (tabIdx) => {
    setLayerData((prev) => {
      const copy = { ...prev };
      copy[layerName][tabIdx] = [
        ...(copy[layerName][tabIdx] || []),
        {
          id: Date.now(),
          name: '',
          userId: null,
          type: 1,
          shares: 0,
          hasPending: false,
          email: '',
          errors: {},
        },
      ];
      return copy;
    });
  };

  const removeShareholder = (tabIdx, id) => {
    setLayerData((prev) => {
      const copy = { ...prev };
      copy[layerName][tabIdx] = copy[layerName][tabIdx].filter(
        (s) => s.id !== id,
      );
      return copy;
    });
  };

  /** ----------------------
   * Fill Application
   * ---------------------- */
  // inside ShareholderLayer
  const handleFillApplication = (tabIdx = null) => {
    let qualified = [];

    if (shareholdersForLayer.length === 0) {
      // Root layer → pick only Corporate/Trust with shares >= 25
      qualified = shareholders.filter(
        (s) =>
          (Number(s.type) === 2 || Number(s.type) === 3) &&
          Number(s.shares) >= 25,
      );
    } else {
      // Sub-layers → collect from all tabs
      Object.values(layerData[layerName]).forEach((tabShareholders = []) => {
        const tabQualified = tabShareholders.filter(
          (s) =>
            (Number(s.type) === 2 || Number(s.type) === 3) &&
            Number(s.shares) >= 25,
        );
        qualified = [...qualified, ...tabQualified];
      });
    }

    if (qualified.length > 0) {
      // 1) Clear layerData forward from this layer
      // 2) Trim layers forward and add fresh next layer
      // onAddLayer(qualified, layerName, layerName);
    }
  };

  useEffect(() => {
    if (!shareholders.length) {
      setIsFormValid(false);
      return;
    }

    const allFilled = shareholders.every(
      (s) =>
        s.name.trim() !== '' &&
        s.email.trim() !== '' &&
        !s.errors?.email &&
        parseFloat(s.shares) > 0,
    );

    const totalShares = calculateSharesCounter(shareholders);
    if (allFilled && Number(totalShares) === 100.0) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [shareholders]);
  const isLayerFormValid = (tabIdx) => {
    const list = layerData[layerName][tabIdx] || [];
    if (!list.length) return false;

    // All rows filled (and no field errors)
    const allFilled = list.every(
      (s) =>
        s.name.trim() !== '' &&
        s.email.trim() !== '' &&
        !s.errors?.email &&
        parseFloat(s.shares) > 0,
    );

    // Shares must equal 100
    const totalShares = calculateSharesCounter(list);

    return allFilled && Number(totalShares).toFixed(2) === '100.00';
  };

  /** ----------------------
   * Render Controls Section
   * ---------------------- */
  const Controls = ({ list, isFirst, tabIdx }) => {
    const numShareholders = list.length;
    const shareCounter = calculateSharesCounter(list);

    return (
      <div className="font-medium flex flex-wrap items-center justify-between px-4 py-3 text-sm">
        {/* Show Records */}
        <div className="flex items-center gap-2">
          <label className="text-[#8C8C8C]">Show Records</label>
          <select
            value={recordsPerPage}
            onChange={(e) => setRecordsPerPage(Number(e.target.value))}
            className="border rounded-lg px-2 py-1 text-black focus:outline-none focus:ring-1 focus:ring-gray-300"
          >
            <option>10</option>
            <option>20</option>
            <option>50</option>
          </select>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            className="p-2 pl-8 border rounded-xl"
            placeholder="Search unit holder/beneficiary"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="absolute left-2 top-3 text-gray-500">
            {' '}
            <img src="/search-normal.svg" alt="Company Logo" className="mr-2" />
          </span>
        </div>
        {/* Shareholders Count */}
        {isFirst && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#464646]">
              NO OF SHAREHOLDERS
            </span>
            <div className="font-semibold">
              <div className="flex items-center text-sm mt-1 md:mt-0 space-x-0 ml-1 border  rounded-md bg-white shadow-md">
                {/* Remove last row */}
                <button
                  onClick={() => {
                    setShareholders((prev) => {
                      if (prev.length === 0) return prev; // avoid error if no rows
                      const lastId = prev[prev.length - 1].id;
                      return prev.filter((s) => s.id !== lastId);
                    });
                  }}
                  className="text-black px-2"
                >
                  <Minus size={18} />
                </button>

                {/* Count display */}
                <span className="text-sm font-medium bg-[#375AE7] text-white p-2">
                  {shareholders.length.toString().padStart(2, '0')}
                </span>

                {/* Add new row */}
                <button
                  onClick={addRootShareholder}
                  className="text-black px-2"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
        {!isFirst && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#464646]">
              NO OF SHAREHOLDERS
            </span>

            <div className="font-semibold">
              <div className="flex items-center text-sm mt-1 md:mt-0 space-x-0 ml-1 border rounded-md bg-white shadow-md">
                {/* Count display */}
                <span className="text-sm font-medium bg-[#375AE7] text-white px-3 py-1">
                  {(layerData[layerName]?.[tabIdx]?.length || 0)
                    .toString()
                    .padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Shares Counter */}
        <div className="flex items-center gap-2 font-medium">
          <span className=" text-[#464646]">SHARES COUNTER</span>
          <div
            className={`${
              shareCounter != 100.0 ? 'text-red-600' : 'text-green-600'
            }
          border rounded-lg px-3 py-1  font-semibold`}
          >
            {shareCounter}%
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border rounded-2xl shadow-sm bg-white mb-6">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div
        onClick={onToggle}
        className="bg-[#375AE7] text-white px-4 py-2 rounded-t-2xl flex justify-between items-center cursor-pointer"
      >
        <h2 className="text-sm font-medium">{layerName}</h2>
        <span className="text-lg">{isOpen ? '−' : '+'}</span>
      </div>

      {isOpen && (
        <>
          {/* CASE 1: Root Layer (no tabs) */}
          {shareholdersForLayer.length === 0 && (
            <div className="p-4 border rounded-b-lg">
              {/* Controls */}
              <Controls list={shareholders} isFirst={true} />
              {calculateSharesCounter(shareholders) != 100.0 && (
                <div className=" flex mx-5 my-2 text-center items-center justify-center  rounded-md px-4 py-2 text-red-500 bg-red-100 border border-red-500">
                  <div className="flex  items-center">
                    <img
                      src="/danger.svg"
                      alt="Arrow"
                      className=" left-0 w-5 h-5 mr-2"
                    />{' '}
                    Missing unit holder/beneficiary information or incorrect
                    percentages entered. Please ensure all unit
                    holder/beneficiary's share percentages are entered
                    accurately until the "Shares Counter" reaches 100%.
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-[#8C8C8C]">
                  <thead className="">
                    <tr className="border-[#EDEDED] border font-medium">
                      <th className="p-2 text-left font-medium"></th>
                      <th className="p-2 text-left font-medium">
                        Unit holder/beneficiary Name
                      </th>
                      <th className="p-2 text-left font-medium">
                        Type Of unit holder/beneficiary
                      </th>
                      <th className="p-2 text-left font-medium">Shares</th>
                      <th className="p-2 text-left font-medium">
                        Email Address
                      </th>
                      <th className="p-2 text-left font-medium">
                        User Registration
                      </th>
                      <th className="p-2"></th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareholders.map((s, i) => (
                      <tr key={s.id} className="border-t font-medium">
                        <td className="p-2 text-[#8C8C8C]">
                          {String(i + 1).padStart(2, '0')}.
                        </td>
                        <td className="p-2 text-black">
                          <input
                            className="border w-full p-2 rounded-xl text-sm"
                            value={s.name}
                            onChange={(e) =>
                              updateRootShareholder(
                                s.id,
                                'name',
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="p-2 text-black">
                          <select
                            className="w-full border p-2 rounded-xl text-sm"
                            value={s.type}
                            onChange={(e) =>
                              updateRootShareholder(
                                s.id,
                                'type',
                                e.target.value,
                              )
                            }
                          >
                            {shareholderTypesData.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2 text-black">
                          <div className="relative w-24">
                            <input
                              type="number"
                              value={s.shares}
                              min="0"
                              onChange={(e) =>
                                updateRootShareholder(
                                  s.id,
                                  'shares',
                                  e.target.value,
                                )
                              }
                              onBlur={(e) => {
                                let value = parseFloat(e.target.value) || 0;
                                updateRootShareholder(
                                  s.id,
                                  'shares',
                                  value.toFixed(2),
                                );
                              }}
                              placeholder="10.00"
                              className={`p-2 pr-8 text-black w-full font-medium border rounded-xl text-md transition-all duration-200 ${'border-gray-300'}`}
                            />
                            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">%</span>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 text-black">
                          <input
                            type="email"
                            className={` ${
                              s.errors?.email ? 'border-[#FF5050]' : ''
                            }
                            border w-full p-2 rounded-xl text-sm`}
                            value={s.email}
                            onChange={(e) => {
                              const value = e.target.value;
                              const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                value,
                              );
                              updateRootShareholder(
                                s.id,
                                'email',
                                e.target.value,
                                {
                                  ...s.errors,
                                  email:
                                    isValid || value === ''
                                      ? ''
                                      : 'Invalid email',
                                },
                              );
                            }}
                          />
                          {s.errors?.email && (
                            <span className="text-red-500 text-xs">
                              {s.errors.email}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          {s.userId != null ? (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                // dispatch(clearStepStatusShareholder());
                                // dispatch(clearCurrentStepShareholder());
                                if (s.hasPending) {
                                  let redirectState = {
                                    path: `/edd-stakeholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                    state: {
                                      from: location.pathname,
                                    },
                                  };
                                  sessionStorage.setItem(
                                    'redirectAfterLogin',
                                    JSON.stringify(redirectState),
                                  );
                                  navigate(
                                    `/edd-stakeholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                    {
                                      state: {
                                        from: location.pathname,
                                      },
                                    },
                                  );
                                } else {
                                  let redirectState = {
                                    path: `/onboarding-shareholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                    state: {
                                      from: location.pathname,
                                    },
                                  };
                                  sessionStorage.setItem(
                                    'redirectAfterLogin',
                                    JSON.stringify(redirectState),
                                  );
                                  navigate(
                                    `/onboarding-shareholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                    {
                                      state: {
                                        from: location.pathname,
                                      },
                                    },
                                  );
                                }
                              }}
                              className="px-3 py-1 rounded-lg shadow-md border text-black font-medium"
                            >
                              Fill Application
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => removeRootShareholder(s.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <img
                              src="/trash.svg"
                              alt="Delete"
                              className="h-4 w-4"
                            />{' '}
                          </button>
                        </td>
                        <td className="p-2">
                          {hasECDDPending(s) ? (
                            <button className="px-4 py-2 rounded-md border border-yellow-600 bg-yellow-50 text-yellow-800 font-medium text-xs uppercase tracking-wide hover:bg-yellow-100 hover:border-yellow-700 transition-all duration-200 shadow-sm">
                              Pending ECDD
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Shareholder */}
              <div className="px-4 py-3 flex justify-center ">
                <button
                  onClick={addRootShareholder}
                  className="flex items-center gap-2 text-[#375AE7] rounded-md font-medium text-sm hover:underline border-2 border-dashed p-2"
                >
                  <img
                    src="/add-darkBlue.svg"
                    alt="Add Icon"
                    className="w-5 h-5"
                  />
                  Add unit holder/beneficiary
                </button>
              </div>
              <div className="flex justify-end px-4 py-4">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setLoading(true);
                    handleAddShareholders(e);
                  }}
                  disabled={!isFormValid}
                  className={`px-6 py-2 rounded-full text-white ${
                    isFormValid
                      ? 'bg-[#375AE7] hover:bg-gray-900'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* CASE 2: Sub-Layers (with tabs) */}
          {shareholdersForLayer.length > 0 && (
            <div className="">
              {/* Tabs */}
              <div className="flex  border-b">
                {shareholdersForLayer.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTab(idx)}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === idx
                        ? 'bg-[#375AE7] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s.parentName}
                  </button>
                ))}
              </div>
              {/* Controls */}
              <Controls
                list={layerData[layerName][activeTab] || []}
                isFirst={false}
                tabIdx={activeTab}
              />
              {/* Tab Content */}

              <div className="p-4 border rounded-b-lg">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-gray-700">
                    <thead className="">
                      <tr className="border-[#EDEDED] border font-medium">
                        <th className="p-2 text-left font-medium"></th>
                        <th className="p-2 text-left font-medium">
                          Unit holder/beneficiary Name
                        </th>
                        <th className="p-2 text-left font-medium">
                          Type Of unit holder/beneficiary
                        </th>
                        <th className="p-2 text-left font-medium">Shares</th>
                        <th className="p-2 text-left font-medium">
                          Email Address
                        </th>
                        <th className="p-2 text-left font-medium">
                          User Registration
                        </th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(layerData[layerName][activeTab] || []).map((s, idx) => (
                        <tr key={s.id} className="border-t font-medium">
                          <td className="p-2 text-[#8C8C8C]">
                            {String(idx + 1).padStart(2, '0')}.
                          </td>
                          <td className="p-2 text-black">
                            <input
                              className="border w-full p-2 rounded-xl text-sm"
                              value={s.name}
                              disabled={true}
                            />
                          </td>
                          <td className="p-2 text-black">
                            <select
                              className="w-full border p-2 rounded-xl text-sm"
                              value={s.type}
                              disabled={true}
                            >
                              {shareholderTypesData.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}{' '}
                            </select>
                          </td>
                          <td className="p-2 text-black">
                            <div className="relative w-24">
                              <input
                                type="number"
                                value={s.shares}
                                disabled={true}
                                onBlur={(e) => {
                                  let value = parseFloat(e.target.value) || 0;
                                  updateShareholder(
                                    activeTab,
                                    idx,
                                    'shares',
                                    value.toFixed(2),
                                  );
                                }}
                                placeholder="10.00"
                                className={`p-2 pr-8 text-black w-full font-medium border rounded-xl text-md transition-all duration-200 ${'border-gray-300'}`}
                              />
                              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-sm">%</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-black">
                            <input
                              type="email"
                              className={` ${
                                s.errors?.email ? 'border-[#FF5050]' : ''
                              }
                            border w-full p-2 rounded-xl text-sm`}
                              value={s.email}
                              disabled={true}
                            />
                            {s.errors?.email && (
                              <span className="text-red-500 text-xs">
                                {s.errors.email}
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            {s.userId != null ? (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  dispatch(clearStepStatusShareholder());
                                  dispatch(clearCurrentStepShareholder());
                                  if (s.hasPending) {
                                    let redirectState = {
                                      path: `/edd-stakeholder//${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                      state: {
                                        from: location.pathname,
                                      },
                                    };
                                    sessionStorage.setItem(
                                      'redirectAfterLogin',
                                      JSON.stringify(redirectState),
                                    );
                                    navigate(
                                      `/edd-stakeholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                      {
                                        state: {
                                          from: location.pathname,
                                        },
                                      },
                                    );
                                  } else {
                                    let redirectState = {
                                      path: `/onboarding-shareholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                      state: {
                                        from: location.pathname,
                                      },
                                    };
                                    sessionStorage.setItem(
                                      'redirectAfterLogin',
                                      JSON.stringify(redirectState),
                                    );
                                    navigate(
                                      `/onboarding-shareholder/${s.id}/${s.type}/shareholder/${false}/${s.name}`,
                                      {
                                        state: {
                                          from: location.pathname,
                                        },
                                      },
                                    );
                                  }
                                }}
                                className="px-3 py-1 rounded-lg shadow-md border text-black font-medium"
                              >
                                View Application
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="p-2">
                            {hasECDDPending(s) ? (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();

                                  let redirectState = {
                                    path: `/edd-stakeholder`,
                                    state: {
                                      from: location.pathname,
                                    },
                                  };
                                  sessionStorage.setItem(
                                    'redirectAfterLogin',
                                    JSON.stringify(redirectState),
                                  );
                                  navigate(`/edd-stakeholder`, {
                                    state: {
                                      from: location.pathname,
                                    },
                                  });
                                }}
                                className="px-4 py-2 rounded-md border border-yellow-600 bg-yellow-50 text-yellow-800 font-medium text-xs uppercase tracking-wide hover:bg-yellow-100 hover:border-yellow-700 transition-all duration-200 shadow-sm"
                              >
                                Pending ECDD
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
const TrusteeInfo = ({ setCurrentStep }) => {
  const formData = useSelector((state) => state.form.formData);
  let signatoryType = useSelector((state) => state.form.fields.signatoryType);
  let signatoryEmail = useSelector((state) => state.form.fields.signatoryEmail);
  let selectedOptions = useSelector(
    (state) => state.form.fields.selectedOptions,
  );
  const [corporates, setCorporates] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const rejectedFields = useSelector((state) => state.form.rejectedFields);
  let fileAttorney = useSelector((state) => state.form.fields.fileAttorney);
  const [shareholderTypesData, setShareholderTypesData] = useState([]);
  let minimumInvestment = useSelector(
    (state) => state.form.fields.minimumInvestment,
  );
  let selected = useSelector((state) => state.form.fields.selected);
  let customDate = useSelector((state) => state.form.fields.customDate);
  let options = useSelector((state) => state.form.fields.options);
  const [treeData, setTreeData] = useState([]);

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showVisualLink, setShowVisualLink] = useState(false);
  const [accountType, setAccountType] = useState(1);
  const stepStatus = useSelector((state) => state.form.stepStatus);
  const [jointAccountUser, setJointAccountUser] = useState(1);
  const [accountTypeLabel, setAccountTypeLabel] = useState('Individual');
  const [refNumber, setRefNumber] = useState(new Array(7).fill(''));
  const [trusteeResponse, setTrusteeResponse] = useState(null);
  const [isAdvisorExist, setIsAdvisorExist] = useState(true);
  const [advisorName, setAdvisorName] = useState('Brian	Vaughan');
  const [fullName, setfullName] = useState('');
  const [formerNames, setFormerNames] = useState([]);
  const [newFormerName, setFormerName] = useState('');
  const [recommendedBy, setRecommendedBy] = useState('');
  const [DoI, setDoI] = useState('');
  const [currencyData, setCurrencyData] = useState([]);
  const [preselectedcashCurrency, setpreselectedCurrency] = useState({
    value: 2,
    label: 'AUD',
  });
  const [preselectedAccountType, setpreselectedAccountType] = useState({
    value: 1,
    label: 'Australian Domestic Company',
  });
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const fieldLabelMap = {
    board_resolution_file: 'Board Resolution',
  };
  const [collapsedLayers, setCollapsedLayers] = useState({}); // { layerName: true/false }
  const typeColors = {
    Corporate: 'linear-gradient(135deg,#34d399,#059669)', // green
    Individual: 'linear-gradient(135deg,#60a5fa,#3b82f6)', // blue
    Trust: 'linear-gradient(135deg,#f472b6,#ec4899)', // pink
    Default: 'linear-gradient(135deg,#a78bfa,#7c3aed)', // purple fallback
  };
  function getNodeStyle(node) {
    const layerColors = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444'];

    // Safely calculate the background gradient
    const color = layerColors[(node.data.layer ?? 0) % layerColors.length]; // fallback to 0 if layer is missing

    const background = `linear-gradient(135deg, ${color}, #000)`; // gradient from color → black

    return {
      background,
      borderRadius: 12,
      padding: '12px 16px',
      color: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      fontWeight: 600,
      fontSize: 14,
      textAlign: 'center',
      minWidth: 140,
      transition: 'transform 0.2s ease',
    };
  }

  const [data, setData] = useState([
    {
      name: 'ABC Company',
      type: 'Corporate',
      children: [
        { name: 'Ann', type: 'Individual', children: [] },
        {
          name: 'XYZ',
          type: 'Corporate',
          children: [
            {
              name: 'DEF',
              type: 'Corporate',
              children: [
                {
                  name: 'Emily Johanson',
                  type: 'Individual',
                  children: [],
                },
                { name: 'Dalia limited', type: 'Corporate', children: [] },
                { name: 'Noah', type: 'Individual', children: [] },
              ],
            },
            { name: 'Aron', type: 'Individual', children: [] },
          ],
        },
        { name: 'Carol', type: 'Trust', children: [] },
      ],
    },
    {
      name: 'You trust',
      type: 'Trust',
      children: [
        {
          name: 'Subsidiary A',
          type: 'Corporate',
          children: [
            { name: 'John Daniel', type: 'Individual', children: [] },
            { name: 'Jane Smith', type: 'Individual', children: [] },
          ],
        },
        { name: 'Subsidiary B', type: 'Trust', children: [] },
      ],
    },
    {
      name: 'John Daniel',
      type: 'Individual',
      children: [],
    },
  ]);

  const handleCollapseAll = () => {
    const newState = {};
    layers.forEach((l) => (newState[l.name] = true)); // true = collapsed
    setCollapsedLayers(newState);
  };

  const handleExpandAll = () => {
    const newState = {};
    layers.forEach((l) => (newState[l.name] = false)); // false = expanded
    setCollapsedLayers(newState);
  };
  const [signatoryTypeLabel, setSignatoryTypeLabel] = useState('');
  const [signatoriesData, setSignatoriesData] = useState([]);
  const { showToast } = useToast();
  const [isUserPopOpen, setisUserPopOpen] = useState(false);
  const [actualData, setactualData] = useState([]);
  const [signatoryText, setSignatoryText] = useState('');
  const [signatoryId, setSignatoryId] = useState('');
  const username = useSelector((state) => state.user.username);
  const [signatoryTypeData, setsignatoryTypeData] = useState([]);

  const [preSelectedSignatoryType, setPreSelectedSignatoryType] = useState({
    value: 1,
    label: '1 to Sign',
  });
  const [preSelectedDurationType, setPreSelectedDurationType] = useState({
    value: 1,
    label: 'Per Day',
  });
  const [members, setMembers] = useState([]);
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null} // handled by react-select
            className="flex-shrink-0 custom-checkbox"
          />
          <span className="truncate">{props.label}</span>
        </div>
      </components.Option>
    );
  };

  // Recursively build nodes & edges from API data
  const [graphNodes, setGraphNodes] = useState([]);
  const [graphEdges, setGraphEdges] = useState([]);
  const getDetailsWithVerification = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/onboarding/trust/trustee-beneficiaries/preview/verifications`,
      );

      if (response.status == 200) {
        setTrusteeResponse(response);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  function mapGraphToTree(node) {
    if (!node) return null;

    const relationshipType = node.relationship?.type?.toLowerCase() || 'root';

    const relationshipLabel =
      relationshipType === 'shareholder'
        ? 'Shareholder'
        : relationshipType === 'director'
          ? 'Director'
          : relationshipType === 'trustee'
            ? 'Trustee'
            : relationshipType === 'beneficiary'
              ? 'Beneficiary'
              : relationshipType === 'root'
                ? 'Root entity'
                : relationshipType.charAt(0).toUpperCase() +
                  relationshipType.slice(1);

    return {
      name: node.full_name || 'Unknown',

      type:
        node.entity_type === 'corporate'
          ? 'Corporate'
          : node.entity_type === 'trust'
            ? 'Trust'
            : 'Individual',

      // Display label inside node
      relationship: relationshipLabel,

      // Used for role colours
      relationshipKey: relationshipType,

      ownership: node.relationship?.percentage_ownership,

      meta: {
        relationship: relationshipType,
        ownership: node.relationship?.percentage_ownership,
      },

      children: node.children?.map(mapGraphToTree) || [],
    };
  }
  useEffect(() => {
    if (graphData?.graph) {
      const formatted = [mapGraphToTree(graphData.graph)];
      setTreeData(formatted);
    }
  }, [graphData]);
  const getOwnershipgraph = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/customer/ownership-graph`);

      if (response.status == 200) {
        setGraphData(response.data);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    const n = [];
    const e = [];
    let idCounter = 0;

    const buildGraph = (node, parentId = null, depth = 0, xOffset = 0) => {
      const id = `${idCounter++}`;
      n.push({
        id,
        data: { label: `${node.name} (${node.type})` },
        position: { x: xOffset * 250, y: depth * 150 },
      });
      if (parentId) {
        e.push({
          id: `${parentId}-${id}`,
          source: parentId,
          target: id,
          animated: true,
        });
      }
      node.children?.forEach((child, idx) =>
        buildGraph(
          child,
          id,
          depth + 1,
          xOffset + idx - node.children.length / 2,
        ),
      );
    };

    data.forEach((root, idx) => buildGraph(root, null, 0, idx * 3)); // <== iterate roots
    setGraphNodes(n);
    setGraphEdges(e);
  }, [data]);

  // Parent state
  const [layers, setLayers] = useState([
    { name: 'Interconnection Layer 01', shareholders: [] },
  ]);
  const [activeTabIndex, setActiveTabIndex] = useState({});
  const [isFocusedAccountFee, setIsFocusedAccountFee] = useState(false);
  const inputRefAccountFee = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();
  const dateInputRef = useRef(null);
  const [companyName, setCompanyName] = useState('Brian	Vaughan');
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };
  // parent component
  const addLayer = (qualified, parentName, afterLayerName) => {
    setLayers((prevLayers) => {
      // 1️⃣ Find index of the layer after which new layer should be added
      const idx = prevLayers.findIndex((l) => l.name === afterLayerName);

      // 2️⃣ Keep layers up to current layer
      const baseLayers = idx >= 0 ? prevLayers.slice(0, idx + 1) : [];

      // 3️⃣ Determine new layer number
      const newLayerNumber = baseLayers.length + 1;
      const newLayerName = `Interconnection Layer ${String(
        newLayerNumber,
      ).padStart(2, '0')}`;

      // 4️⃣ Reset shareholder fields for next layer
      const resetShareholders = qualified.map((q) => ({
        id: Date.now() + Math.random(),
        name: q.name, // optional: keep name for tab label
        type: q.type || 'Individual',
        shares: 0,
        userId: null,
        email: '',
        hasPending: false,
        errors: {},
      }));

      const newLayer = {
        name: newLayerName,
        shareholders: resetShareholders,
        parent: parentName,
      };

      return [...baseLayers, newLayer];
    });

    // 5️⃣ Reset active tab indices for trimmed layers and new layer
    setActiveTabIndex((prevTabs) => {
      const newLayerNumber = Object.keys(prevTabs).length + 1;
      const newLayerName = `Interconnection Layer ${String(
        newLayerNumber,
      ).padStart(2, '0')}`;

      return {
        ...prevTabs,
        [afterLayerName]: 0, // reset current layer tab to first
        [newLayerName]: 0, // new layer starts at first tab
      };
    });
  };
  const formatNumber = (value) => {
    // Remove commas before parsing
    const number = parseFloat(value.toString().replace(/,/g, ''));

    if (isNaN(number)) return '';

    // Format with 2 decimal places and add commas
    return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const countryOptions = [
    { code: 'AU', name: 'Australia', dial: '+61' },
    { code: 'US', name: 'United States', dial: '+1' },
    { code: 'IN', name: 'India', dial: '+91' },
    { code: 'GB', name: 'United Kingdom', dial: '+44' },
    // Add more countries as needed
  ];
  const getEnums = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/onboarding/trust/trustee-beneficiaries/enums`,
      );

      if (response.status == 200) {
        const shareholderTypes = [];
        response.data.shareholder_types.forEach((profile) => {
          shareholderTypes.push({
            value: profile.id,
            label: profile.name,
          });
        });
        setShareholderTypesData(shareholderTypes);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          setsignatoryTypeData([]);
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
      setsignatoryTypeData([]);
    }
  };
  const getCompanyName = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/onboarding/trust/trust-information/preview/verification`,
      );

      if (response.status == 200) {
        setCompanyName(response.data.trust_full_name.value);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (stepStatus.length > 0 && stepStatus[1].status != 'DRAFT') {
      getDetailsWithVerification();
    }
  }, [stepStatus]);
  useEffect(() => {
    setMembers((prev) => {
      if (prev.length === 0) return prev;

      return prev.map((member, i) => ({
        ...member,
        managingMember: i === 0 ? 'yes' : 'no', // first one selected by default
      }));
    });
    getLayeredData();
    getEnums();
    getCompanyName();
    getOwnershipgraph();
  }, []); // run only on component mount
  useEffect(() => {
    setMembers((prev) => {
      // Check if any member is already selected
      const isAnySelected = prev.some((m) => m.managingMember === 'yes');

      if (!isAnySelected && prev.length > 0) {
        // No selection yet → select the first member
        return prev.map((member, i) => ({
          ...member,
          managingMember: i === 0 ? 'yes' : 'no',
        }));
      }

      // Otherwise, keep existing selection
      return prev;
    });
  }, [members.length]); // watch only the length, not full array

  const resetApplicationAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/onboarding/trust/trustee-beneficiaries/reset`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleReset = async () => {
    let toastMessage = null;
    try {
      const response = await resetApplicationAPI();
      if (response.status == 200) {
        toastMessage = {
          message: response.message,
          type: 'success',
        };
        getDetailsWithVerification();
        const responseStatus = await GetAPIcalls(`/api/v1/onboarding/status`);
        if (responseStatus.status == 200) {
          if (responseStatus.data.length > 0) {
            dispatch(setStepStatus(responseStatus.data));
          }
        }
        const responseRejected = await GetAPIcalls(
          `/api/v1/onboarding/rejected-fields`,
        );
        if (responseRejected.status == 200) {
          dispatch(setRejectedFields(responseRejected.data)); // <-- Save in Redux
        }
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const handleBack = () => {
    setCurrentStep(1);
  };
  const getIndividuals = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/onboarding/trust/trustee-beneficiaries/trustees/individual`,
      );

      if (response.status == 200) {
        let mapped = response.data.map((item) => ({
          firstName: item.first_name, // or Date.now() if you want local-only IDs
          lastName: item.last_name,
          countryCode: item.country_code, // 1 = Individual, 2 = Corporate, 3 = Trust
          mobile: item.mobile_number,
          email: item.email,
          managing: item.managing_member,
          beneficialOwner: item.beneficial_owner,
        }));
        return mapped;
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          /* no-op */
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getCorporates = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/onboarding/trust/trustee-beneficiaries/trustees/corporate`,
      );

      if (response.status == 200) {
        setCorporates(
          response.data.map((item) => ({
            companyName: item.company_name, // or Date.now() if you want local-only IDs
            beneficialOwner: item.beneficial_owner,
            corporateId: item.corporate_user_id,
            directors: item?.directors,
            expanded: false,
            status: item.status,
          })),
        );
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          /* no-op */
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const handleContinue = async (isFinal) => {
    let individuals = await getIndividuals();
    let corporates = await getCorporates();
    if (individuals && individuals?.length > 0) {
      if (signatoryType === 1 && !signatoryEmail) {
        showToast('Please select a signatory.', 'error');
        return;
      }
      if (signatoryType === 2 && selectedOptions.length === 0) {
        showToast('Please select at least one signatory.', 'error');
        return;
      }
      if (!fileAttorney.value) {
        showToast('Please upload board resolution document.', 'error');
        return;
      }
      if (selected === 'custom' && !customDate.value) {
        showToast('Please select valid expiry date.', 'error');
        return;
      }
    }
    if (
      (individuals && individuals?.length > 0) ||
      (corporates && corporates?.length > 0)
    ) {
      setLoading(true);
      handlesave(isFinal);
    } else {
      showToast('Please add atleast one trustee');
      return;
    }
  };
  const submitApplicationAPI = async (isFinal) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let individuals = await getIndividuals();

    let apiUrl = `${BASE_URL}/api/v1/onboarding/trust/trustee-beneficiaries/submit`;
    let bodyData = {};
    if (individuals.length > 0) {
      let signatories =
        signatoryType == 1
          ? [signatoryEmail]
          : signatoryType === 2
            ? selectedOptions.map((item) => item.email)
            : options.map((item) => item.email);
      bodyData = {
        signatory_type: signatoryType,
        signatories: signatories,
        board_resolution_file: {
          file_id: fileAttorney.value.file_id,
          file_name: fileAttorney.value.file_name,
        },
        is_submitted: isFinal,
      };
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const buildLayersFromApi = (apiLayers) => {
    return apiLayers.map((layer) => {
      // ROOT
      if (layer.layer_number === 1) {
        return {
          name: 'Interconnection Layer 1',
          layerNumber: 1,
          shareholders: [], // root has NO tabs
          rootEntities: layer.entities || [],
        };
      }

      // SUB LAYERS
      return {
        name: `Interconnection Layer ${layer.layer_number}`,
        layerNumber: layer.layer_number,
        shareholders: layer.tabs.map((t) => ({
          parentId: t.parent_id,
          parentName: t.parent_name,
          data: t.group.beneficiaries,
        })),
      };
    });
  };
  const getLayeredData = async () => {
    const response = await GetAPIcalls(
      '/api/v1/onboarding/trust/trustee-beneficiaries/beneficiaries/layered',
    );

    if (response.status === 200) {
      if (response.data.layers.length > 0) {
        const uiLayers = buildLayersFromApi(response.data.layers);
        setLayers(uiLayers);
      }
    }
  };
  const handlesave = async (isFinal) => {
    let toastMessage = null;
    try {
      const response = await submitApplicationAPI(isFinal);
      if (response.status == 200) {
        if (isFinal) {
          setCurrentStep(3);
          toastMessage = {
            message: response.message,
            type: 'success',
          };
          const responseStatus = await GetAPIcalls(`/api/v1/onboarding/status`);
          if (responseStatus.status == 200) {
            if (responseStatus.data.length > 0) {
              dispatch(setStepStatus(responseStatus.data));
            }
          }
          const responseRejected = await GetAPIcalls(
            `/api/v1/onboarding/rejected-fields`,
          );
          if (responseRejected.status == 200) {
            dispatch(setRejectedFields(responseRejected.data)); // <-- Save in Redux
          }
        } else {
          toastMessage = {
            message:
              'Information has been saved successfully. Please submit to proceed further.',
            type: 'success',
          };
        }
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-100px)] lg:min-w-[80vw] lg:max-w-[80vw]">
      <div className="flex-grow  overflow-visible mb-4">
        {loading && (
          <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        {stepStatus.length > 0 &&
          (stepStatus[1].status == 'IN_REVIEW' ||
            stepStatus[1].status == 'COMPLETED') && (
            <div className="mb-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 rounded-md text-sm flex items-center">
              <img
                src="/warning-circle.svg"
                alt="Info"
                className="w-4 h-4 mr-2"
              />
              <span>
                This section is{' '}
                <strong>in review by our onboarding team</strong>. You will be
                notified once the verification is complete.{' '}
              </span>
            </div>
          )}

        {stepStatus.length > 0 && stepStatus[1].status == 'APPROVED' && (
          <div className="mb-2 bg-green-100 border-l-4 border-green-500 text-green-700 p-3 rounded-md text-sm flex items-center">
            <span>
              This section has been <strong>approved</strong> by our onboarding
              team.
            </span>
          </div>
        )}
        {rejectedFields?.TRUSTEES_BENEFICIARIES?.rejected_fields?.length >
          0 && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="font-semibold text-red-700 mb-1">
              {rejectedFields.TRUSTEES_BENEFICIARIES.rejected_fields.length ===
              1
                ? 'The following field requires correction:'
                : 'The following fields require correction:'}{' '}
            </p>

            <div className="flex flex-wrap gap-2">
              {rejectedFields.TRUSTEES_BENEFICIARIES.rejected_fields.map(
                (field) => (
                  <span
                    key={field}
                    className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded"
                  >
                    {fieldLabelMap[field] || field.replace(/_/g, ' ')}
                  </span>
                ),
              )}
            </div>
          </div>
        )}
        <TrusteesPanel
          containerClass="lg:min-w-[80vw] lg:max-w-[80vw]"
          isEdit={true}
          trusteeName={companyName}
          isShareholder={false}
          trusteeResponse={trusteeResponse}
          handleContinue={handleContinue}
        />
        <div className="bg-white shadow-md rounded-md p-4 mt-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0 px-4">
            <div className="flex items-center space-x-2">
              <img src="/people.svg" alt="User Avatar" className="h-6 w-6" />
              <h1 className="text-xl font-medium">
                Unit Holders/Beneficiaries:
                <span className="text-xl font-medium text-[#8C8C8C] ml-1">
                  {companyName}
                </span>
              </h1>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setShowVisualLink(true);
                }}
                className="bg-white text-black text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
              >
                <img
                  src="/hashtag.svg"
                  alt="Collapse Icon"
                  className="w-4 h-4 flex-shrink-0"
                />
                <span className="font-medium">Visual Link</span>
              </button>
              <button
                onClick={handleCollapseAll}
                className="bg-white text-black text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
              >
                <img
                  src="/arrow-square-up.svg"
                  alt="Collapse Icon"
                  className="w-4 h-4 flex-shrink-0"
                />
                <span className="font-medium">Collapse All</span>
              </button>
              <button
                onClick={handleExpandAll}
                className="bg-[#375AE7] text-white text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
              >
                <img
                  src="/arrow-square-down.svg"
                  alt="Collapse Icon"
                  className="w-4 h-4 flex-shrink-0"
                />
                <span className="font-medium">Expand All</span>
              </button>
            </div>
          </div>
          {showVisualLink && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[99999]">
              <div className="relative bg-white  w-[95%] md:w-[900px] p-6">
                {/* Close Button */}
                <button
                  onClick={() => setShowVisualLink(false)}
                  className="absolute top-3 right-3 text-gray-600 hover:text-black text-2xl font-bold transition-colors"
                >
                  &times;
                </button>

                {/* Graph */}
                <div className="mt-6 w-full h-[500px] overflow-hidden">
                  <MindmapTree data={treeData} />
                </div>
              </div>
            </div>
          )}
          <ul className="space-y-2 pl-6 pr-2 text-xs font-medium text-black list-disc">
            <li>
              Please provide unit Holders/beneficiaries details for your company
              here.
            </li>
            <li>
              If a corporate entity or trust holds 25% or more shares, we'll
              create interconnection layers to identify the unit
              holders/beneficiaries of those entities. This will continue until
              we identify the individual ultimate beneficial owner of your
              company.
            </li>
            <li>
              Please complete the application for any corporate or trust unit
              holders/beneficiaries who owns 25% or more shares. Failure to do
              so will prevent you from continuing the application.
            </li>
            <li>
              The application for the individual ultimate beneficial owners will
              be sent to the email address you provide here, To obtain the
              additional information Ratified requires.
            </li>
          </ul>
          <div className="p-6">
            {layers.map((layer) => (
              <ShareholderLayer
                key={layer.layerNumber}
                layerName={layer.name}
                shareholdersForLayer={layer.shareholders}
                rootEntities={layer.rootEntities}
                shareholderTypesData={shareholderTypesData}
                collapsed={collapsedLayers[layer.name] ?? false}
                getLayeredData={getLayeredData}
                onToggle={() =>
                  setCollapsedLayers((p) => ({
                    ...p,
                    [layer.name]: !p[layer.name],
                  }))
                }
              />
            ))}
          </div>
        </div>
      </div>
      <footer className=" mt-4 flex flex-col flex-shrink-0 md:flex-row p-4 bg-[#375AE7] z-50">
        {stepStatus.length > 0 &&
          stepStatus[1].status != 'COMPLETED' &&
          stepStatus[1].status != 'APPROVED' &&
          stepStatus[1].status != 'IN_REVIEW' && (
            <>
              <div className="flex flex-col md:flex-row items-center justify-start w-full space-y-2 md:space-y-0 relative">
                <ConfirmResetButton
                  onConfirm={handleReset}
                  isReset={true}
                  isBack={true}
                  onBack={handleBack}
                />
              </div>
              <div className="bottom-actions flex flex-col md:flex-row mt-2 md:mt-0 items-center justify-end w-full space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex flex-col md:flex-row items-center justify-end w-full space-y-2 md:space-y-0 md:space-x-4 relative">
                  {/* Save as Draft */}
                  <button
                    onClick={() => {
                      handleContinue(false);
                    }}
                    className="flex w-full md:w-auto items-center justify-center space-x-2 border-2 border-white text-white font-medium py-2 px-4 rounded-full text-sm"
                  >
                    <span>Save as Draft</span>
                  </button>

                  <ConfirmContinueButton
                    onContinue={() => handleContinue(true)}
                    buttonText="Continue"
                    confirmTitle="Save Section"
                    confirmMessage="Are you sure, you want to continue. This action will ensure, the saved information is un-editable. Therefore please make sure you have provided the correct details."
                  />
                </div>
              </div>
            </>
          )}
        {stepStatus.length > 0 &&
          (stepStatus[1].status == 'COMPLETED' ||
            stepStatus[1].status == 'APPROVED' ||
            stepStatus[1].status == 'IN_REVIEW') && (
            <>
              <div className="flex flex-col md:flex-row items-center justify-start w-full space-y-2 md:space-y-0 relative">
                <ConfirmResetButton
                  onConfirm={handleReset}
                  isReset={false}
                  isBack={true}
                  onBack={handleBack}
                />
              </div>
              <div className="bottom-actions flex flex-col md:flex-row mt-2 md:mt-0 items-center justify-end w-full space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex flex-col md:flex-row items-center justify-end w-full space-y-2 md:space-y-0 md:space-x-4 relative">
                  {/* Save as Draft */}
                  <button
                    onClick={() => {
                      setCurrentStep(3);
                    }}
                    className="flex w-full md:w-auto items-center justify-center space-x-2 border-2 border-white text-white font-medium py-2 px-4 rounded-full text-sm"
                  >
                    <span>Next</span>
                  </button>
                </div>
              </div>
            </>
          )}
      </footer>
    </div>
  );
};

export default TrusteeInfo;
